/**
 * RKHDetailModal Component
 * Displays detailed RKH information including foreman groups, harvesters, and locations
 */

import React from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Button,
    Box,
    Text,
    Flex,
    Badge,
    Divider,
    Spinner,
    VStack,
    HStack,
    Heading,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Grid,
    GridItem,
    useColorModeValue,
} from '@chakra-ui/react';
import { RKHDetail, RKH_STAGE_LABELS, RKH_STAGE_COLORS } from '../types';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';

interface RKHDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    data: RKHDetail | null;
    loading: boolean;
    error: string | null;
}

const RKHDetailModal: React.FC<RKHDetailModalProps> = ({
    isOpen,
    onClose,
    data,
    loading,
    error,
}) => {
    const bgCard = useColorModeValue('gray.50', 'gray.700');
    const borderColor = useColorModeValue('gray.200', 'gray.600');

    const formatDate = (dateStr: string) => {
        try {
            return format(new Date(dateStr), 'dd MMMM yyyy', { locale: id });
        } catch {
            return dateStr;
        }
    };

    const formatDateTime = (dateStr: string) => {
        try {
            return format(new Date(dateStr), 'dd/MM/yyyy HH:mm', { locale: id });
        } catch {
            return dateStr;
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent maxH="90vh">
                <ModalHeader borderBottom="1px" borderColor={borderColor}>
                    Detail RKH
                </ModalHeader>
                <ModalCloseButton />
                
                <ModalBody py={6}>
                    {loading ? (
                        <Flex justify="center" align="center" minH="200px">
                            <Spinner size="xl" color="blue.500" />
                        </Flex>
                    ) : error ? (
                        <Flex justify="center" align="center" minH="200px">
                            <Text color="red.500">{error}</Text>
                        </Flex>
                    ) : data ? (
                        <VStack spacing={6} align="stretch">
                            {/* Header Info */}
                            <Box bg={bgCard} p={4} borderRadius="lg" border="1px" borderColor={borderColor}>
                                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={4}>
                                    <GridItem>
                                        <Text fontSize="sm" color="gray.600" fontWeight="medium">Nomor RKH</Text>
                                        <Text fontSize="lg" fontWeight="bold" color="blue.600">{data.rkh_nbr}</Text>
                                    </GridItem>
                                    <GridItem>
                                        <Text fontSize="sm" color="gray.600" fontWeight="medium">Tanggal RKH</Text>
                                        <Text fontSize="md" fontWeight="semibold">{formatDate(data.rkh_date)}</Text>
                                    </GridItem>
                                    <GridItem>
                                        <Text fontSize="sm" color="gray.600" fontWeight="medium">Stage</Text>
                                        <Badge colorScheme={RKH_STAGE_COLORS[data.stage]} fontSize="sm" mt={1}>
                                            {RKH_STAGE_LABELS[data.stage]}
                                        </Badge>
                                    </GridItem>
                                    <GridItem>
                                        <Text fontSize="sm" color="gray.600" fontWeight="medium">Estate</Text>
                                        <Text fontSize="md" fontWeight="semibold">{data.estate_name}</Text>
                                    </GridItem>
                                    <GridItem>
                                        <Text fontSize="sm" color="gray.600" fontWeight="medium">Divisi</Text>
                                        <Text fontSize="md" fontWeight="semibold">{data.division_name}</Text>
                                    </GridItem>
                                    <GridItem>
                                        <Text fontSize="sm" color="gray.600" fontWeight="medium">Terakhir Sync</Text>
                                        <Text fontSize="sm">{data.date_sync ? formatDateTime(data.date_sync) : '-'}</Text>
                                    </GridItem>
                                </Grid>
                            </Box>

                            <Divider />

                            {/* Foreman Groups & Harvesters */}
                            <Box>
                                <Heading size="md" mb={4} color="gray.700">
                                    Kemandoran ({data.foreman_groups.length} Kemandoran)
                                </Heading>
                                <Accordion allowMultiple>
                                    {data.foreman_groups.map((group, idx) => (
                                        <AccordionItem key={group.id} border="1px" borderColor={borderColor} borderRadius="md" mb={3}>
                                            <AccordionButton bg={bgCard} _hover={{ bg: 'gray.100' }}>
                                                <Box flex="1" textAlign="left">
                                                    <HStack spacing={3}>
                                                        <Text fontWeight="bold">{group.foreman_group_name}</Text>
                                                        <Text fontWeight="medium">
                                                            Mandor: {group.foreman_name}
                                                        </Text>
                                                        <Badge colorScheme="green" ml="auto">
                                                            {group.harvesters.length} Pemanen
                                                        </Badge>
                                                    </HStack>
                                                </Box>
                                                <AccordionIcon />
                                            </AccordionButton>
                                            <AccordionPanel pb={4}>
                                                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3} mb={4}>
                                                    <Box>
                                                        <Text fontSize="sm" color="gray.600">Mandor 1</Text>
                                                        <Text fontWeight="medium">{group.foreman1_name}</Text>
                                                    </Box>
                                                    <Box>
                                                        <Text fontSize="sm" color="gray.600">Kerani Panen</Text>
                                                        <Text fontWeight="medium">{group.kerani_harvest_name}</Text>
                                                    </Box>
                                                </Grid>
                                                
                                                <Text fontWeight="semibold" mb={2} color="gray.700">Daftar Pemanen:</Text>
                                                <Box overflowX="auto">
                                                    <Table size="sm" variant="simple">
                                                        <Thead bg="gray.100">
                                                            <Tr>
                                                                <Th>NIP</Th>
                                                                <Th>Nama</Th>
                                                                <Th>Jabatan</Th>
                                                                <Th>Level Pekerjaan</Th>
                                                                <Th>Asistensi</Th>
                                                                <Th>Clock In</Th>
                                                                <Th>clock Out</Th>
                                                                <Th>Kode Absensi</Th>
                                                            </Tr>
                                                        </Thead>
                                                        <Tbody>
                                                            {group.harvesters.map((harvester) => (
                                                                <Tr key={harvester.id}>
                                                                    <Td fontWeight="medium">{harvester.nip}</Td>
                                                                    <Td>{harvester.name}</Td>
                                                                    <Td>{harvester.job_name}</Td>
                                                                    <Td>{harvester.job_level_name}</Td>
                                                                    <Td>
                                                                        <Badge colorScheme={harvester.is_asistensi ? 'yellow' : 'gray'}>
                                                                            {harvester.is_asistensi ? 'Ya' : 'Tidak'}
                                                                        </Badge>
                                                                    </Td>
                                                                    <Td>{harvester.time_in ? formatDateTime(harvester.time_in) : '-'}</Td>
                                                                    <Td>{harvester.time_out ? formatDateTime(harvester.time_out) : '-'}</Td>
                                                                    <Td>{harvester.attendance_code || '-'}</Td>
                                                                </Tr>
                                                            ))}
                                                        </Tbody>
                                                    </Table>
                                                </Box>
                                            </AccordionPanel>
                                        </AccordionItem>
                                    ))}
                                </Accordion>
                            </Box>

                            <Divider />
                        </VStack>
                    ) : (
                        <Text>Tidak ada data</Text>
                    )}
                </ModalBody>

                <ModalFooter borderTop="1px" borderColor={borderColor}>
                    <Button colorScheme="blue" onClick={onClose}>
                        Tutup
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default RKHDetailModal;
